import { apiService } from '@app/shared/services/api.service';
import type {
  BusinessCatalogSubscription,
  CatalogSubscriptionPayment,
} from '@neogeniux/api-contracts';

const BASE = '/billing/me';

export interface PlanChangePreview {
  fromPlanCode: string;
  fromPlanName: string;
  toPlanCode: string;
  toPlanName: string;
  fromMonthlyAmount: string;
  toMonthlyAmount: string;
  currency: string;
  isDowngrade: boolean;
  appliesImmediately: boolean;
  scheduledFor: string | null;
  todayInvoice: {
    adjustmentAmount: string;
    subtotalAmount: string;
    taxAmount: string;
    taxRatePercent: string;
    totalAmount: string;
    daysRemaining: number;
    daysInPeriod: number;
    periodStart: string | null;
    periodEnd: string | null;
  };
  grantsToRevoke: Array<{
    id: string;
    kind: 'module' | 'addon' | 'feature' | 'quota';
    targetCode: string;
    compValueDop: string | null;
    isPerpetual: boolean;
  }>;
}

export const selfBillingService = {
  getMine: (): Promise<BusinessCatalogSubscription | null> =>
    apiService.getInstance().get(BASE),

  listPayments: (): Promise<CatalogSubscriptionPayment[]> =>
    apiService.getInstance().get(`${BASE}/payments`),

  checkout: (input: {
    planTemplateCode: string;
    billingInterval?: 'monthly' | 'annual';
    currency?: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<{ sessionId: string; url: string | null }> =>
    apiService.getInstance().post(`${BASE}/checkout`, input),

  changePlan: (
    planTemplateCode: string
  ): Promise<BusinessCatalogSubscription> =>
    apiService.getInstance().post(`${BASE}/change-plan`, { planTemplateCode }),

  previewPlanChange: (planTemplateCode: string): Promise<PlanChangePreview> =>
    apiService
      .getInstance()
      .get(`${BASE}/plan-change-preview`, { params: { planTemplateCode } }),

  cancel: (atPeriodEnd: boolean): Promise<BusinessCatalogSubscription> =>
    apiService.getInstance().post(`${BASE}/cancel`, { atPeriodEnd }),

  resume: (): Promise<BusinessCatalogSubscription> =>
    apiService.getInstance().post(`${BASE}/resume`, {}),

  setBillingInterval: (
    interval: 'monthly' | 'annual'
  ): Promise<{
    interval: 'monthly' | 'annual';
    invoice: { id: string; invoiceNumber: string; total: string } | null;
    currentPeriodEnd: string | null;
  }> => apiService.getInstance().post(`${BASE}/billing-interval`, { interval }),
};
